// =====================================================================
// src/components/intl-acca/NationFlag.tsx
// =====================================================================
// Real national-flag SVG tile. Every WC2026 nation has a dedicated,
// validated public-domain SVG vendored at /public/flags/<CODE>.svg
// (FIFA 3-letter code). The bicolor kit-monogram is now ONLY a
// genuine fallback if a flag asset is somehow missing/unreachable —
// it is no longer the primary rendering.
//
// Honest by design: nation === null still renders the neutral grey
// placeholder (not a guess).

import { useState } from "react";
import type { Nation } from "../../db/intl-acca-types-extended";

interface NationFlagProps {
  nation: Nation | null;
  size?: number; // px — defaults to 36
}

export function NationFlag({ nation, size = 36 }: NationFlagProps): JSX.Element {
  const [imgFailed, setImgFailed] = useState(false);

  if (!nation) {
    return (
      <div
        className="ia-flag"
        style={{
          width: size,
          height: size,
          background: "var(--bg-elev-2)",
          border: "1px dashed var(--border)",
        }}
        aria-label="Unknown nation"
      />
    );
  }

  // Primary: the dedicated flag SVG.
  if (!imgFailed) {
    return (
      <div
        className="ia-flag"
        role="img"
        aria-label={nation.name}
        style={{ width: size, height: size, overflow: "hidden" }}
      >
        <img
          src={`/flags/${nation.code}.svg`}
          alt={nation.name}
          width={size}
          height={size}
          loading="lazy"
          decoding="async"
          onError={() => setImgFailed(true)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </div>
    );
  }

  // Fallback ONLY if the asset is genuinely missing: kit monogram.
  return (
    <div
      className="ia-flag"
      role="img"
      aria-label={nation.name}
      style={{
        width: size,
        height: size,
        background: `linear-gradient(135deg,
          ${nation.kit_primary} 0%,
          ${nation.kit_primary} 60%,
          ${nation.kit_secondary} 60%,
          ${nation.kit_secondary} 100%)`,
      }}
    >
      <span className="ia-flag-text" style={{ fontSize: size * 0.42 }}>
        {nation.short}
      </span>
    </div>
  );
}
